import React from "react";
import { useTranslation } from "react-i18next";
import { translateColumnLabel } from "../domain/labels";
import { findRow } from "../domain/find";
import { selectDocument, selectSelection } from "../domain/selectors";
import { useEditorActions, useEditorStore } from "../domain/redux/hooks";
import { columnPreviewFlexWeights } from "../domain/column-presets";

export function ColumnPreviewStrip({ rowId }: { rowId: string }) {
  const { t } = useTranslation();
  const document = useEditorStore(selectDocument);
  const selection = useEditorStore(selectSelection);
  const { setSelection } = useEditorActions();
  const row = findRow(document, rowId);
  if (!row || row.columns.length <= 1) {
    return null;
  }

  const selectedColumnId =
    selection.kind === "column" && selection.rowId === rowId
      ? selection.columnId
      : undefined;

  const flexWeights = columnPreviewFlexWeights(row.columnWidths);

  function selectColumn(columnId: string) {
    if (selectedColumnId === columnId) {
      setSelection({ kind: "row", rowId });
      return;
    }
    setSelection({ kind: "column", rowId, columnId });
  }

  return (
    <div className={"email-builder-settings-column-preview-wrap"}>
      <div className={"email-builder-settings-column-preview"} role="group" aria-label={t("Columns")}>
        {row.columns.map((column, index) => {
          const widthWeight = flexWeights[index] ?? 1 / row.columns.length;
          const selected = column.id === selectedColumnId;
          return (
            <button
              key={column.id}
              type="button"
              className={"email-builder-settings-column-preview-bar"}
              data-selected={selected ? "true" : "false"}
              style={{ flex: `${widthWeight} 1 0` }}
              aria-label={
                selected
                  ? t("Back to row layout")
                  : t("Select {{column}} {{number}}", {
                      column: translateColumnLabel(t).toLowerCase(),
                      number: index + 1,
                    })
              }
              aria-pressed={selected}
              onClick={() => selectColumn(column.id)}
            >
              {index + 1}
            </button>
          );
        })}
      </div>
    </div>
  );
}
